import { useCallback, useEffect, useRef } from 'react';

import { useLogEvent, useShallowStableEvent } from './core';

import type { Event } from './core';
import type { ReactNode } from 'react';

export const useInViewedEvent = (target: Event['target']) => {
  const inViewDedupedRef = useRef(new Set());
  // useInViewedEvent 需要有 key 來去重複的機制，避免 UI 元素消失後「再次」出現在畫面，所以使用 Set 來處理
  const handleLogEvent = useLogEvent('inView', target);

  const log = useCallback(
    (props: Event['props'], { key }: { key?: string } = {}) => {
      if (key && inViewDedupedRef.current.has(key)) {
        return;
      }

      handleLogEvent(props);

      key && inViewDedupedRef.current.add(key);
    },
    [handleLogEvent],
  );

  return log;
};

export const EventInView = ({
  children,
  target,
  props = {},
  isReady = true,
}: {
  props?: Event['props'];
  target: Event['target'];
  children: ReactNode;
  isReady?: boolean;
}) => {
  const inView = useInViewedEvent(target);
  const stabledEvent = useShallowStableEvent({ props });

  useEffect(() => {
    if (!isReady) return;
    if (!stabledEvent.props) return;

    isReady && inView(stabledEvent.props);
  }, [inView, isReady, stabledEvent]);

  return children;
};

export const useClickEvent = (target: Event['target']) => {
  const handleClickEvent = useLogEvent('click', target);

  const log = useCallback(handleClickEvent, [handleClickEvent]);

  return log;
};
